import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../../api/axios';

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const AcceptInvitePage = () => {
  const query = useQuery();
  const token = query.get('token');
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');

  // Fetch invite email for display (optional, if backend supports it)
  useEffect(() => {
    const fetchInvite = async () => {
      if (!token) return;
      try {
        const response = await api.post('/invite/lookup', { invite_token: token });
        if (response.data && response.data.email) {
          setInviteEmail(response.data.email);
        }
      } catch (e) {
        // Ignore, just don't show email
      }
    };
    fetchInvite();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!password || !confirmPassword) {
      setError('Please enter and confirm your password.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      const response = await api.post('/invite/accept', {
        invite_token: token,
        password
      });
      if (response.data.status === 'success') {
        setSuccess('Account created! Redirecting to login...');
        setTimeout(() => navigate('/login'), 2000);
      } else {
        setError(response.data.message || 'Failed to accept invite.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to accept invite.');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-white p-8 rounded-xl shadow-xl text-center">
          <h2 className="text-xl font-display-bold mb-2 text-red-600">Invalid Invite Link</h2>
          <p className="text-gray-600 font-body">No invite token found in the URL.</p>
          <button
            className="mt-6 px-4 py-2 bg-blue-600 text-white rounded-lg font-body-semibold hover:bg-blue-700 transition-colors"
            onClick={() => navigate('/login')}
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-palamuru-blue via-palamuru-blue/90 to-palamuru-slate flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo and Brand */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-gradient-to-r from-palamuru-saffron to-palamuru-gold rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-strong">
            <span className="text-white font-display-black text-3xl">P</span>
          </div>
          <h1 className="font-display-bold text-3xl text-white mb-2">Palamuru Constructions</h1>
          <p className="font-elegant text-lg text-white/90">Welcome! Activate your account below.</p>
        </div>
        <div className="bg-white/95 backdrop-blur-sm rounded-3xl shadow-strong border border-white/20 p-8">
          <h2 className="font-display-bold text-2xl text-gray-800 mb-4 text-center">Accept Your Invite</h2>
          {inviteEmail && (
            <div className="mb-4 text-center">
              <span className="inline-block bg-gray-100 text-gray-700 px-3 py-1 rounded-full font-body text-sm">{inviteEmail}</span>
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-body-medium text-gray-700 mb-1">Password</label>
              <input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-palamuru-blue/50 focus:border-palamuru-blue transition-all duration-200 font-body"
                required
                placeholder="Create a password (min 6 characters)"
              />
            </div>
            <div>
              <label className="block text-sm font-body-medium text-gray-700 mb-1">Confirm Password</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-palamuru-blue/50 focus:border-palamuru-blue transition-all duration-200 font-body"
                required
                placeholder="Re-enter your password"
              />
            </div>
            {error && <div className="text-red-600 text-sm font-body-medium">{error}</div>}
            {success && <div className="text-green-600 text-sm font-body-medium">{success}</div>}
            <button
              type="submit"
              className="w-full bg-gradient-to-r from-palamuru-saffron to-palamuru-gold text-white font-body-bold py-3 px-6 rounded-xl shadow-soft hover:shadow-medium transform hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              disabled={loading}
            >
              {loading ? 'Creating Account...' : 'Set Password & Activate'}
            </button>
          </form>
          <div className="mt-6 text-center">
            <button
              type="button"
              className="font-body-semibold text-palamuru-blue hover:text-palamuru-saffron transition-colors"
              onClick={() => navigate('/login')}
            >
              Back to Login
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AcceptInvitePage; 